<template>
  <div v-if="showModule" class="documents-module">
    <Toast position="bottom-center" />

    <input
      ref="fileInput"
      type="file"
      class="documents-hidden-input"
      @change="onFileSelected"
    />

    <div v-if="loading" class="documents-state">
      <div class="documents-skeleton">
        <div class="documents-skeleton-card">
          <div class="documents-skeleton-header">
            <Skeleton width="12rem" height="1rem" borderRadius="999px" />
            <Skeleton width="2rem" height="2rem" borderRadius="999px" />
          </div>
          <div class="documents-skeleton-grid">
            <Skeleton width="100%" height="12rem" borderRadius="20px" />
            <Skeleton width="100%" height="12rem" borderRadius="20px" />
          </div>
        </div>
        <div class="documents-skeleton-card">
          <div class="documents-skeleton-header">
            <Skeleton width="11rem" height="1rem" borderRadius="999px" />
            <Skeleton width="2rem" height="2rem" borderRadius="999px" />
          </div>
          <div class="documents-skeleton-grid documents-skeleton-grid--single">
            <Skeleton width="100%" height="9rem" borderRadius="20px" />
          </div>
        </div>
        <div class="documents-skeleton-card">
          <div class="documents-skeleton-header">
            <Skeleton width="8rem" height="1rem" borderRadius="999px" />
            <Skeleton width="2rem" height="2rem" borderRadius="999px" />
          </div>
          <Skeleton width="100%" height="4.5rem" borderRadius="18px" />
        </div>
      </div>
    </div>

    <div v-else-if="error" class="documents-state">
      <div class="documents-error">
        <EmptyState
          :title="t('wkt.client.documents.error-state.title')"
          :description="t('wkt.client.documents.error-state.description')"
        />
        <Button :label="t('wkt.client.documents.actions.retry')" @click="loadDocumentsInfo" />
      </div>
    </div>

    <div v-else class="documents-content">
      <section v-if="requestDocuments.length" class="documents-section">
        <div class="documents-section-panel">
          <header class="documents-section-header">
            <div class="documents-section-heading">
              <h2>{{ t("wkt.client.documents.section.requests") }}</h2>
            </div>
            <span class="documents-section-count">{{ requestDocuments.length }}</span>
          </header>

          <div class="documents-request-grid">
            <article
              v-for="request in requestDocuments"
              :key="request.id"
              class="documents-request-card"
            >
              <button
                type="button"
                class="documents-request-profile"
                @click="showEmployeeDetails(request.creatorId)"
              >
                <img
                  v-if="creatorAvatarUrl(request)"
                  :src="creatorAvatarUrl(request)"
                  :alt="requestSenderName(request)"
                  class="documents-request-avatar"
                />
                <div v-else class="documents-request-avatar documents-request-avatar--fallback">
                  {{ requestSenderInitials(request) }}
                </div>

                <div class="documents-request-profile-copy">
                  <span class="documents-request-name">{{ requestSenderName(request) }}</span>
                  <span v-if="formatDateTime(request.creationDate)" class="documents-request-date">
                    {{ formatDateTime(request.creationDate) }}
                  </span>
                </div>
              </button>

              <div class="documents-request-heading">
                <i :class="['documents-request-icon', requestIconClass(request)]" aria-hidden="true"></i>
                <div class="documents-request-title-group">
                  <h3>{{ requestDisplayTitle(request) }}</h3>
                  <p v-if="request.requestMessage" class="documents-request-message">
                    {{ request.requestMessage }}
                  </p>
                </div>
              </div>

              <div class="documents-request-actions">
                <Button
                  v-if="isUploadRequest(request)"
                  text
                  severity="secondary"
                  icon="pi pi-upload"
                  :loading="busyRequestId === request.id"
                  :label="t('wkt.client.documents.attach_document')"
                  @click="openUploadPicker(request)"
                />
                <Button
                  v-else
                  text
                  severity="secondary"
                  icon="pi pi-eye"
                  :label="t('wkt.client.documents.view_document')"
                  @click="handlePrimaryRequestAction(request)"
                />
                <Button
                  v-if="isSignatureRequest(request)"
                  icon="pi pi-pencil"
                  :loading="busyRequestId === request.id"
                  :label="t('wkt.client.common.dialog-buttons.sign')"
                  @click="openSignatureDialog(request)"
                />
              </div>
            </article>
          </div>
        </div>
      </section>

      <section v-if="pendingReviews.length" class="documents-section">
        <div class="documents-section-panel">
          <header class="documents-section-header">
            <div class="documents-section-heading">
              <h2>{{ t("wkt.client.documents.section.reviews") }}</h2>
            </div>
            <span class="documents-section-count">{{ pendingReviews.length }}</span>
          </header>

          <div class="documents-review-grid">
            <DocumentAssessmentReviewCard
              v-for="review in pendingReviews"
              :key="review.id"
              :review="review"
              @open="openAssessmentReview"
              @show-phase1="openPhase1Submission"
              @show-employee="showEmployeeDetails"
            />
          </div>
        </div>
      </section>

      <section class="documents-section">
        <div class="documents-section-panel">
          <header class="documents-section-header">
            <div class="documents-section-heading">
              <h2>{{ t("wkt.client.documents.section.categories") }}</h2>
            </div>
            <span class="documents-section-count">{{ categoryCards.length }}</span>
          </header>

          <div v-if="categoryCards.length" class="documents-category-list">
            <button
              v-for="category in categoryCards"
              :key="category.id"
              type="button"
              class="documents-category-item"
              @click="openCategory(category.id, category.name)"
            >
              <span class="documents-category-name">{{ category.name }}</span>
              <span v-if="category.unreadCount > 0" class="documents-category-badge">
                {{ category.unreadCount }}
              </span>
              <span class="material-icons documents-category-chevron">chevron_right</span>
            </button>
          </div>
          <div v-else class="documents-empty">
            <EmptyState
              :title="t('wkt.client.documents.empty-state.title')"
              :description="t('wkt.client.documents.empty-state.description')"
            />
          </div>
        </div>
      </section>
    </div>

    <Dialog
      v-model:visible="signatureDialogVisible"
      modal
      :closable="!signatureLoading"
      :header="t('wkt.client.documents.signature.dialog.title')"
      class="documents-signature-dialog"
      @hide="resetSignatureDialog"
    >
      <div class="documents-signature-dialog-body">
        <p class="documents-signature-copy">{{ signatureDescription }}</p>

        <div v-if="requiresCodeSignature" class="documents-signature-code">
          <FloatLabel variant="on">
            <InputText
              id="documents-signature-code"
              v-model="signCode"
              :disabled="signatureLoading || !signCodeRequested"
              class="documents-signature-input"
            />
            <label for="documents-signature-code">{{ t("wkt.client.documents.signature.code_label") }}</label>
          </FloatLabel>
        </div>

        <div
          v-else-if="requiresHandwrittenSignature"
          class="documents-signature-pad-shell"
        >
          <canvas
            ref="signatureCanvas"
            class="documents-signature-pad"
            width="560"
            height="240"
            @pointerdown="beginSignature"
            @pointermove="drawSignature"
            @pointerup="endSignature"
            @pointerleave="endSignature"
          />
          <Button
            text
            severity="secondary"
            :style="signatureSecondaryButtonStyle"
            :label="t('wkt.client.documents.clear_signature')"
            @click="clearSignaturePad"
          />
        </div>
      </div>

      <template #footer>
        <Button
          text
          severity="secondary"
          :style="signatureSecondaryButtonStyle"
          :disabled="signatureLoading"
          :label="t('wkt.client.common.dialog-buttons.cancel')"
          @click="signatureDialogVisible = false"
        />
        <Button
          :style="signaturePrimaryButtonStyle"
          :loading="signatureLoading"
          :label="signaturePrimaryActionLabel"
          @click="submitSignature"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { gwtApi } from "@wkt/api/gwt/gwtAPI";
import EmptyState from "@wkt/components/common/EmptyState.vue";
import { useAlert } from "@wkt/composables/useAlert";
import { useUtils } from "@wkt/composables/useUtils";
import documentsApi from "@wkt/api/documents/documentsAPI";
import type {
  DocumentCategoryDTO,
  DocumentsInfoDTO,
  DocumentsModulePayload,
  FullAssessmentReviewDTO,
  FullDocumentDTO,
} from "@wkt/api/documents";
import type { SessionInformation } from "@wkt/api/gwt/gwtDTO";
import DocumentAssessmentReviewCard from "./DocumentAssessmentReviewCard.vue";

const DOCUMENT_TYPE_FILE = 0;
const DOCUMENT_TYPE_URL = 1;
const DOCUMENT_TYPE_REQUEST = 2;
const DOCUMENT_TYPE_SIGNATURE = 3;
const DOCUMENT_TYPE_PROCEDURE = 4;
const DOCUMENT_TYPE_FORM = 5;

const REQUEST_STATUS_PENDING = 1;

const SIGNATURE_MODE_SMS = 1;
const SIGNATURE_MODE_HANDWRITTEN = 2;
const SIGNATURE_MODE_WHATSAPP = 3;

interface CategoryCard {
  id: number;
  name: string;
  unreadCount: number;
}

const showModule = ref(false);
const loading = ref(true);
const error = ref(false);
const documents = ref<FullDocumentDTO[]>([]);
const categories = ref<DocumentCategoryDTO[]>([]);
const pendingReviews = ref<FullAssessmentReviewDTO[]>([]);
const busyRequestId = ref<number | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const uploadRequest = ref<FullDocumentDTO | null>(null);
const signatureDialogVisible = ref(false);
const signatureLoading = ref(false);
const currentSignatureRequest = ref<FullDocumentDTO | null>(null);
const signCodeRequested = ref(false);
const signCode = ref("");
const signatureCanvas = ref<HTMLCanvasElement | null>(null);
const hasSignature = ref(false);

let isDrawing = false;

const { t } = useI18n();
const { showAlert } = useAlert();
const { getProfileAssetUrl } = useUtils();

function isPayload(value: unknown): value is DocumentsModulePayload {
  return Boolean(value) && typeof value === "object";
}

function getSessionCategories(): DocumentCategoryDTO[] {
  const sessionInformation = gwtApi.getSessionInformation() as
    | (SessionInformation & { documentCategories?: DocumentCategoryDTO[] })
    | null;

  return Array.isArray(sessionInformation?.documentCategories)
    ? sessionInformation.documentCategories
    : [];
}

function applyInfo(info?: DocumentsInfoDTO | null, payloadCategories?: DocumentCategoryDTO[]): void {
  documents.value = Array.isArray(info?.documents) ? info.documents : [];
  pendingReviews.value = Array.isArray(info?.pendingReviews) ? info.pendingReviews : [];
  categories.value = Array.isArray(payloadCategories) && payloadCategories.length > 0
    ? payloadCategories
    : getSessionCategories();
  loading.value = false;
  error.value = false;
}

function applyPayload(payload?: unknown): void {
  if (isPayload(payload) && payload.loading) {
    loading.value = true;
    error.value = false;
    return;
  }

  if (!isPayload(payload)) {
    loadDocumentsInfo();
    return;
  }

  applyInfo(payload, payload.categories);

  if (!payload.documents && !payload.pendingReviews) {
    loadDocumentsInfo();
  }
}

function loadDocumentsInfo(): void {
  loading.value = true;
  error.value = false;

  documentsApi.getDocumentsInfo(
    (response) => {
      applyInfo(response);
    },
    () => {
      error.value = true;
      loading.value = false;
    }
  );
}

function formatDateTime(value?: string | null): string {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function requestSenderName(request: FullDocumentDTO): string {
  const directName = [request.firstname, request.lastname].filter(Boolean).join(" ").trim();
  if (directName) {
    return directName;
  }

  const companyName = gwtApi.getSessionInformation()?.company?.name;
  return companyName || "---";
}

function requestSenderInitials(request: FullDocumentDTO): string {
  const parts = requestSenderName(request).split(" ").filter(Boolean);
  return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("") || "?";
}

function creatorAvatarUrl(request: FullDocumentDTO): string {
  return getProfileAssetUrl(request.profilePicture);
}

function isRequestDocument(document: FullDocumentDTO): boolean {
  return document.documentPath == null && (
    document.documentType === DOCUMENT_TYPE_REQUEST ||
    document.documentType === DOCUMENT_TYPE_SIGNATURE ||
    (document.documentType === DOCUMENT_TYPE_FORM && document.requestStatus === REQUEST_STATUS_PENDING)
  );
}

function isVisibleDocument(document: FullDocumentDTO): boolean {
  if (isRequestDocument(document)) {
    return false;
  }

  return Boolean(document.showDocument);
}

function isUploadRequest(document: FullDocumentDTO): boolean {
  return document.documentType === DOCUMENT_TYPE_REQUEST;
}

function isSignatureRequest(document: FullDocumentDTO): boolean {
  return document.documentType === DOCUMENT_TYPE_SIGNATURE;
}

function requestIconClass(document: FullDocumentDTO): string {
  switch (document.documentType) {
    case DOCUMENT_TYPE_FILE:
      return "pi pi-file";
    case DOCUMENT_TYPE_URL:
      return "pi pi-link";
    case DOCUMENT_TYPE_REQUEST:
      return "pi pi-upload";
    case DOCUMENT_TYPE_SIGNATURE:
      return "pi pi-pencil";
    case DOCUMENT_TYPE_PROCEDURE:
      return "pi pi-briefcase";
    case DOCUMENT_TYPE_FORM:
      return "pi pi-list-check";
    default:
      return "pi pi-exclamation-triangle";
  }
}

function requestDisplayTitle(document: FullDocumentDTO): string {
  return (
    document.requestTitle?.trim() ||
    document.documentName?.trim() ||
    document.templateName?.trim() ||
    document.formName?.trim() ||
    t("wkt.client.documents.view_document")
  );
}

function unreadCountForCategory(categoryId: number): number {
  return documents.value.filter((document) => {
    if (!isVisibleDocument(document) || !document.employeeId || document.documentShowDate) {
      return false;
    }

    if (categoryId === 0) {
      return document.documentCategoryId == null;
    }

    return document.documentCategoryId === categoryId;
  }).length;
}

const requestDocuments = computed(() => documents.value.filter(isRequestDocument));

const categoryCards = computed<CategoryCard[]>(() => {
  const cards: CategoryCard[] = [];
  const unclassifiedCount = documents.value.filter(
    (document) => document.documentCategoryId == null && isVisibleDocument(document)
  ).length;

  if (unclassifiedCount > 0) {
    cards.push({
      id: 0,
      name: t("wkt.client.documents.unclassified_category"),
      unreadCount: unreadCountForCategory(0),
    });
  }

  categories.value.forEach((category) => {
    if (category.id == null) {
      return;
    }

    const count = documents.value.filter(
      (document) => document.documentCategoryId === category.id && isVisibleDocument(document)
    ).length;

    if (count === 0) {
      return;
    }

    cards.push({
      id: category.id,
      name: category.name,
      unreadCount: unreadCountForCategory(category.id),
    });
  });

  return cards;
});

function openCategory(categoryId: number, categoryName: string): void {
  window.location.hash = `#docbycategory;categoryId=${encodeURIComponent(String(categoryId))};categoryName=${encodeURIComponent(categoryName)}`;
}

function showEmployeeDetails(employeeId?: number | null): void {
  if (!employeeId) {
    return;
  }

  gwtApi.profileShowEmployeeDetails(employeeId);
}

function openFormViewer(documentId: number): void {
  window.location.hash = `#form;formId=${encodeURIComponent(String(documentId))}`;
}

function openAssessmentReview(review: FullAssessmentReviewDTO): void {
  const phase = getAssessmentReviewPhase(review);
  if (!phase) {
    return;
  }

  window.location.hash = `#review;phase=${phase};reviewId=${encodeURIComponent(String(review.id))}`;
}

function openPhase1Submission(review: FullAssessmentReviewDTO): void {
  window.location.hash = `#review;phase=1;reviewId=${encodeURIComponent(String(review.id))}`;
}

function getAssessmentReviewPhase(review: FullAssessmentReviewDTO): number {
  if (!review.phase1CompletionDate && (review.assessmentType === 1 || review.assessmentType === 3)) {
    return 1;
  }

  if (!review.phase2CompletionDate && (review.assessmentType === 2 || review.assessmentType === 3)) {
    return 2;
  }

  return 0;
}

function handlePrimaryRequestAction(request: FullDocumentDTO): void {
  if (request.documentType === DOCUMENT_TYPE_FORM) {
    openFormViewer(request.id);
    return;
  }

  const companyId =
    window.localStorage.getItem("worktime.client.company.id") ||
    String(gwtApi.getSessionInformation()?.company?.id || "");

  gwtApi.downloadFile(
    `user/getDocumentTemplate/${companyId}/${request.id}`,
    request.templateName || request.documentName || `document-${request.id}.pdf`
  );
}

function openUploadPicker(request: FullDocumentDTO): void {
  uploadRequest.value = request;
  fileInput.value?.click();
}

function onFileSelected(event: Event): void {
  const target = event.target as HTMLInputElement | null;
  const selectedFile = target?.files?.[0];
  const request = uploadRequest.value;

  if (!selectedFile || !request) {
    return;
  }

  busyRequestId.value = request.id;

  documentsApi.uploadDocument(
    selectedFile,
    request.id,
    () => {
      busyRequestId.value = null;
      uploadRequest.value = null;
      if (target) {
        target.value = "";
      }
      showAlert({
        text: t("wkt.client.documents.document_uploaded_successfully"),
        color: "success",
      });
      loadDocumentsInfo();
    },
    (requestError) => {
      busyRequestId.value = null;
      uploadRequest.value = null;
      if (target) {
        target.value = "";
      }

      const responseData = requestError?.response?.data as { message?: string | null } | undefined;
      showAlert({
        text: responseData?.message || requestError?.message || t("wkt.client.documents.error-state.description"),
        color: "error",
        timeout: 6000,
      });
    }
  );
}

function openSignatureDialog(request: FullDocumentDTO): void {
  currentSignatureRequest.value = request;
  signCodeRequested.value = false;
  signCode.value = "";
  hasSignature.value = false;
  signatureDialogVisible.value = true;
}

const requiresCodeSignature = computed(() =>
  currentSignatureRequest.value?.signatureMode === SIGNATURE_MODE_SMS ||
  currentSignatureRequest.value?.signatureMode === SIGNATURE_MODE_WHATSAPP
);

const requiresHandwrittenSignature = computed(
  () => currentSignatureRequest.value?.signatureMode === SIGNATURE_MODE_HANDWRITTEN
);

const signatureDescription = computed(() => {
  const request = currentSignatureRequest.value;
  if (!request) {
    return "";
  }

  if (request.signatureMode === SIGNATURE_MODE_SMS) {
    return `${t("wkt.client.documents.enter_sms_code_to sign_document")} ${getMaskedSignaturePhoneSuffix(request)}`.trim();
  }

  if (request.signatureMode === SIGNATURE_MODE_WHATSAPP) {
    return `${t("wkt.client.documents.enter_whatsapp_code_to_sign_document")} ${getMaskedSignaturePhoneSuffix(request)}`.trim();
  }

  if (request.signatureMode === SIGNATURE_MODE_HANDWRITTEN) {
    return t("wkt.client.documents.draw_rubric_to sign_document");
  }

  return "";
});

const signaturePrimaryActionLabel = computed(() => {
  if (requiresCodeSignature.value && !signCodeRequested.value) {
    return t("wkt.client.common.dialog-buttons.continue");
  }

  return t("wkt.client.common.dialog-buttons.sign");
});

const signaturePrimaryButtonStyle = {
  background: "var(--primary-color, #185ea9)",
  borderColor: "var(--primary-color, #185ea9)",
  color: "#ffffff",
};

const signatureSecondaryButtonStyle = {
  background: "rgba(var(--primary-rgb, 24, 94, 169), 0.06)",
  borderColor: "rgba(var(--primary-rgb, 24, 94, 169), 0.22)",
  color: "var(--primary-color, #185ea9)",
};

function getMaskedSignaturePhoneSuffix(request: FullDocumentDTO): string {
  const mobile = request.signatureMobileNumber?.trim();
  if (!mobile) {
    return "";
  }

  return mobile.length > 8 ? mobile.substring(8) : mobile;
}

function resetSignatureDialog(): void {
  currentSignatureRequest.value = null;
  signCodeRequested.value = false;
  signCode.value = "";
  signatureLoading.value = false;
  clearSignaturePad();
}

function submitSignature(): void {
  const request = currentSignatureRequest.value;
  if (!request) {
    return;
  }

  signatureLoading.value = true;
  busyRequestId.value = request.id;

  if (requiresCodeSignature.value) {
    if (!signCodeRequested.value) {
      documentsApi.requestSignCode(
        request.id,
        (response) => {
          signatureLoading.value = false;
          busyRequestId.value = null;

          if (response?.rsOK) {
            signCodeRequested.value = true;
            return;
          }

          showAlert({
            text: response?.message || t("wkt.client.documents.not-signed"),
            color: "error",
            timeout: 6000,
          });
        },
        (requestError) => {
          signatureLoading.value = false;
          busyRequestId.value = null;
          const responseData = requestError?.response?.data as { message?: string | null } | undefined;
          showAlert({
            text: responseData?.message || requestError?.message || t("wkt.client.documents.not-signed"),
            color: "error",
            timeout: 6000,
          });
        }
      );
      return;
    }

    if (!signCode.value.trim()) {
      signatureLoading.value = false;
      busyRequestId.value = null;
      showAlert({
        text: t("wkt.client.documents.sms_code_invalid"),
        color: "error",
      });
      return;
    }

    documentsApi.validateSignCode(
      {
        documentId: request.id,
        validationCode: signCode.value.trim(),
      },
      (response) => {
        signatureLoading.value = false;
        busyRequestId.value = null;

        if (response?.rsOK) {
          onSignatureCompleted();
          return;
        }

        handleSignCodeFailure(response?.resultCode || 0);
      },
      () => {
        signatureLoading.value = false;
        busyRequestId.value = null;
        handleSignCodeFailure(0);
      }
    );
    return;
  }

  if (requiresHandwrittenSignature.value) {
    if (!hasSignature.value || !signatureCanvas.value) {
      signatureLoading.value = false;
      busyRequestId.value = null;
      showAlert({
        text: t("wkt.client.documents.signature_required"),
        color: "error",
      });
      return;
    }

    documentsApi.uploadSignaturePicture(
      request.id,
      signatureCanvas.value.toDataURL(),
      () => {
        signatureLoading.value = false;
        busyRequestId.value = null;
        onSignatureCompleted();
      },
      () => {
        signatureLoading.value = false;
        busyRequestId.value = null;
        showAlert({
          text: t("wkt.client.documents.not-signed"),
          color: "error",
          timeout: 6000,
        });
      }
    );
    return;
  }

  signatureLoading.value = false;
  busyRequestId.value = null;
}

function handleSignCodeFailure(resultCode: number): void {
  switch (resultCode) {
    case -4:
      showAlert({ text: t("wkt.client.documents.not-authorized"), color: "error", timeout: 6000 });
      break;
    case -3:
      showAlert({ text: t("wkt.client.documents.not-found"), color: "error", timeout: 6000 });
      break;
    case -2:
      showAlert({ text: t("wkt.client.documents.sms_code_invalid"), color: "error" });
      break;
    case -1:
      showAlert({ text: t("wkt.client.documents.not-valid"), color: "error", timeout: 6000 });
      break;
    default:
      showAlert({ text: t("wkt.client.documents.not-signed"), color: "error", timeout: 6000 });
      break;
  }
}

function onSignatureCompleted(): void {
  showAlert({
    text: t("wkt.client.documents.document_signed_successfully"),
    color: "success",
  });
  signatureDialogVisible.value = false;
  loadDocumentsInfo();
}

function initSignaturePad(): void {
  const canvas = signatureCanvas.value;
  if (!canvas) {
    return;
  }

  const context = canvas.getContext("2d");
  if (!context) {
    return;
  }

  context.lineCap = "round";
  context.lineJoin = "round";
  context.lineWidth = 2.5;
  context.strokeStyle = "#0f172a";

  clearSignaturePad();
}

function clearSignaturePad(): void {
  const canvas = signatureCanvas.value;
  const context = canvas?.getContext("2d");
  if (!canvas || !context) {
    hasSignature.value = false;
    return;
  }

  context.clearRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = "#f8fafc";
  context.fillRect(0, 0, canvas.width, canvas.height);
  hasSignature.value = false;
}

function getCanvasPoint(event: PointerEvent): { x: number; y: number } | null {
  const canvas = signatureCanvas.value;
  if (!canvas) {
    return null;
  }

  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) {
    return null;
  }

  return {
    x: (event.clientX - rect.left) * (canvas.width / rect.width),
    y: (event.clientY - rect.top) * (canvas.height / rect.height),
  };
}

function beginSignature(event: PointerEvent): void {
  if (!requiresHandwrittenSignature.value) {
    return;
  }

  const canvas = signatureCanvas.value;
  const context = canvas?.getContext("2d");
  const point = getCanvasPoint(event);
  if (!canvas || !context || !point) {
    return;
  }

  canvas.setPointerCapture?.(event.pointerId);
  context.beginPath();
  context.moveTo(point.x, point.y);
  isDrawing = true;
  event.preventDefault();
}

function drawSignature(event: PointerEvent): void {
  if (!isDrawing) {
    return;
  }

  const context = signatureCanvas.value?.getContext("2d");
  const point = getCanvasPoint(event);
  if (!context || !point) {
    return;
  }

  context.lineTo(point.x, point.y);
  context.stroke();
  hasSignature.value = true;
  event.preventDefault();
}

function endSignature(event?: PointerEvent): void {
  if (event && signatureCanvas.value?.hasPointerCapture?.(event.pointerId)) {
    signatureCanvas.value.releasePointerCapture(event.pointerId);
  }
  isDrawing = false;
}

watch(signatureDialogVisible, async (visible) => {
  if (visible && requiresHandwrittenSignature.value) {
    await nextTick();
    initSignaturePad();
  }
});

function onReveal(payload?: unknown): void {
  showModule.value = true;
  applyPayload(payload);
}

function refreshModule(): void {
  loadDocumentsInfo();
}

defineExpose({
  onReveal,
  refreshModule,
});
</script>

<style scoped>
.documents-module {
  --documents-surface: #ffffff;
  --documents-border: rgba(203, 213, 225, 0.72);
  --documents-subtle: #64748b;
  --documents-text: #0f172a;
  --documents-hover: rgba(24, 94, 169, 0.05);
  min-height: 100%;
  background: #ffffff;
}

.documents-hidden-input {
  display: none;
}

.documents-state {
  min-height: calc(100vh - 110px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 20px 40px;
  background: #ffffff;
}

.documents-skeleton,
.documents-error {
  width: min(1100px, 100%);
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.documents-skeleton-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 24px;
  border: 1px solid var(--documents-border);
  border-radius: 24px;
  background: var(--documents-surface);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.04);
}

.documents-skeleton-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.documents-skeleton-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.documents-skeleton-grid--single {
  grid-template-columns: 1fr;
}

.documents-content {
  width: min(1100px, 100%);
  margin: 0 auto;
  padding: 8px 16px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.documents-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.documents-section-panel {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 22px;
  border: 1px solid var(--documents-border);
  border-radius: 24px;
  background: var(--documents-surface);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.04);
}

.documents-section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.documents-section-heading {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.documents-section-header h2 {
  margin: 0;
  font-size: 1.02rem;
  font-weight: 700;
  color: var(--documents-text);
}

.documents-section-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  height: 34px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(24, 94, 169, 0.1);
  color: var(--primary-color, #185ea9);
  font-size: 0.86rem;
  font-weight: 700;
}

.documents-section-empty {
  padding-top: 6px;
}

.documents-request-grid,
.documents-review-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.documents-request-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  min-height: 248px;
  border-radius: 20px;
  border: 1px solid rgba(226, 232, 240, 0.9);
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
}

.documents-request-profile {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.documents-request-avatar {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  object-fit: cover;
  background: #e2e8f0;
}

.documents-request-avatar--fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: var(--documents-text);
}

.documents-request-profile-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.documents-request-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--documents-text);
}

.documents-request-date {
  font-size: 0.9rem;
  color: var(--documents-subtle);
}

.documents-request-heading {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  min-width: 0;
}

.documents-request-title-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.documents-request-heading h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--documents-text);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.documents-request-icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 14px;
  color: var(--primary-color, #185ea9);
  background: rgba(24, 94, 169, 0.1);
}

.documents-request-message {
  margin: 0;
  color: #475569;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.documents-request-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-top: auto;
}

.documents-category-list {
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 20px;
  overflow: hidden;
  background: #ffffff;
}

.documents-category-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 10px;
  min-height: 56px;
  padding: 0 18px;
  border: 0;
  border-bottom: 1px solid rgba(226, 232, 240, 0.9);
  background: #ffffff;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.18s ease;
}

.documents-category-item:last-child {
  border-bottom: 0;
}

.documents-category-item:hover {
  background: var(--documents-hover);
}

.documents-category-name {
  font-size: 0.96rem;
  font-weight: 500;
  color: var(--documents-text);
}

.documents-category-badge {
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  background: #dc2626;
  color: #ffffff;
  font-size: 0.78rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.documents-category-chevron {
  color: var(--primary-color, #185ea9);
}

.documents-empty {
  padding: 18px 0 0;
}

.documents-signature-dialog :deep(.p-dialog-content) {
  overflow: visible;
}

.documents-signature-dialog :deep(.p-dialog) {
  border-radius: 20px;
  overflow: hidden;
}

.documents-signature-dialog :deep(.p-dialog-header) {
  padding: 1.1rem 1.4rem 0.75rem;
}

.documents-signature-dialog-body {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.documents-signature-copy {
  margin: 0;
  color: #475569;
  line-height: 1.5;
}

.documents-signature-input {
  width: 100%;
}

.documents-signature-pad-shell {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.documents-signature-pad {
  width: 100%;
  min-height: 180px;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.16);
  background: #f8fafc;
  touch-action: none;
}

.documents-signature-dialog :deep(.p-dialog-footer) {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0.9rem 1.4rem 1.2rem;
}

.documents-signature-dialog :deep(.p-button.p-button-secondary),
.documents-signature-dialog :deep(.p-button.p-button-text.p-button-secondary) {
  color: var(--primary-color, #185ea9);
  border-color: rgba(var(--primary-rgb, 24, 94, 169), 0.22);
  background: rgba(var(--primary-rgb, 24, 94, 169), 0.06);
}

.documents-signature-dialog :deep(.p-button.p-button-secondary:hover),
.documents-signature-dialog :deep(.p-button.p-button-text.p-button-secondary:hover) {
  color: var(--primary-color, #185ea9);
  border-color: rgba(var(--primary-rgb, 24, 94, 169), 0.28);
  background: rgba(var(--primary-rgb, 24, 94, 169), 0.1);
}

.documents-signature-dialog :deep(.p-button.p-button-text:not(.p-button-secondary)) {
  color: var(--primary-color, #185ea9);
}

.documents-module :deep(.p-button) {
  border-radius: 12px;
  font-weight: 600;
}

.documents-module :deep(.p-button:not(.p-button-text):not(.p-button-link):not(.p-button-secondary)) {
  background: var(--primary-color, #185ea9);
  border-color: var(--primary-color, #185ea9);
}

.documents-module :deep(.p-button:not(.p-button-text):not(.p-button-link):not(.p-button-secondary):hover) {
  background: var(--primary-dark-color, #0056b3);
  border-color: var(--primary-dark-color, #0056b3);
}

.documents-module :deep(.p-button.p-button-text) {
  color: var(--primary-color, #185ea9);
}

@media (max-width: 767px) {
  .documents-state {
    min-height: calc(100vh - 90px);
    padding: 12px 12px 24px;
  }

  .documents-content {
    padding: 8px 8px 24px;
    gap: 18px;
  }

  .documents-skeleton-grid {
    grid-template-columns: 1fr;
  }

  .documents-section-panel {
    padding: 18px 16px;
    border-radius: 20px;
  }

  .documents-request-grid,
  .documents-review-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .documents-request-card {
    padding: 16px;
    border-radius: 16px;
    min-height: auto;
  }

  .documents-request-actions {
    justify-content: stretch;
  }

  .documents-request-actions :deep(.p-button) {
    width: 100%;
  }

  .documents-category-list {
    border-radius: 16px;
  }

  .documents-category-item {
    padding: 0 14px;
  }
}
</style>
